import dayjs from "dayjs";
import type { RobotRaw, RobotTimeline } from "../types/Robot";

/** Structurally identical to `TimeWindowPicker`'s `TimeWindow` — duplicated here (rather than
 *  imported) because 1-Domains must not depend on 5-UI/GUI. TypeScript's structural typing
 *  means values returned from here are assignable wherever the GUI `TimeWindow` is expected. */
export interface TimeWindow {
	start: Date;
	end: Date;
}

const BACKFILL_CLAMP_MS = 2 * 60 * 1000;

/** The fixed local day a "Live" session streams: local midnight of `now` through local
 *  midnight of the next day. Pass `now` for test determinism (matches `RangeCache`'s `now`
 *  opt pattern) — defaults to the real current time. */
export const liveDayWindow = (now?: Date): TimeWindow => ({
	start: dayjs(now).startOf("day").toDate(),
	end: dayjs(now).add(1, "day").startOf("day").toDate(),
});

/** Clamps a backfill fetch's end to `now - 2min` (never before `dayStart`) so the initial
 *  backfill never reaches into the live edge that range caches assume is immutable. */
export const clampBackfillEnd = (dayStart: Date, now: Date): Date => {
	const clamped = now.getTime() - BACKFILL_CLAMP_MS;
	return clamped > dayStart.getTime() ? new Date(clamped) : dayStart;
};

/** Hard ceiling on how far back a live raw poll may reach. The poll normally starts at
 *  `watermark - lookback`, but the watermark only advances when a tick returns points
 *  (`liveStreamReducer` ignores empty ticks), so an idle robot pins it at its last sample and
 *  the polled window grows by the poll interval every poll interval. A session left open on a
 *  robot that stopped reporting reached a FOUR-DAY raw window re-queried every 10s, which is
 *  what pushed the beta API into repeated OOM kills. Anything older than this ceiling is the
 *  one-shot backfill's job, not the poll's. */
export const MAX_RAW_POLL_SPAN_MS = 15 * 60 * 1000;

/** Start of a live raw poll: `watermarkMs - lookbackMs`, floored at `now - MAX_RAW_POLL_SPAN_MS`
 *  so a stale watermark can never widen the window without bound. Pass `now` for test
 *  determinism — defaults to the real current time. */
export const rawPollStart = (watermarkMs: number, lookbackMs: number, now: number = Date.now()): Date =>
	new Date(Math.max(watermarkMs - lookbackMs, now - MAX_RAW_POLL_SPAN_MS));

/** True when `now` falls within `[window.start, window.end]` (inclusive) — the "does this
 *  loaded window bracket the present?" gate behind OpsMap's passive "go live" pill (shown for
 *  windows like today's shift, Today, or Last 24h). `window` may be absent (no timeline loaded
 *  yet), which is never live. Pass `now` for test determinism — defaults to the real current time. */
export const windowIncludesNow = (window: TimeWindow | null | undefined, now: Date = new Date()): boolean => {
	if (!window) return false;
	const t = now.getTime();
	return window.start.getTime() <= t && t <= window.end.getTime();
};

export interface MergeRawPointsResult {
	merged: RobotRaw[];
	watermarkMs: number;
}

/** Two-pointer sorted merge of `existing` and `incoming` raw points (both assumed sorted
 *  ascending by timestamp per the `Robot.raw` invariant), deduplicating by timestamp —
 *  on a tie, the `existing` entry is kept. `watermarkMs` is the max of `watermarkFloorMs`
 *  and the last merged timestamp, so an empty backfill still reports a sensible watermark
 *  (the caller-supplied floor, e.g. dayStart). */
export const mergeRawPoints = (
	existing: RobotRaw[],
	incoming: RobotRaw[],
	watermarkFloorMs: number,
): MergeRawPointsResult => {
	if (incoming.length === 0) {
		return { merged: existing, watermarkMs: watermarkFloorMs };
	}

	const merged: RobotRaw[] = [];
	let i = 0;
	let j = 0;
	while (i < existing.length && j < incoming.length) {
		const a = existing[i];
		const b = incoming[j];
		if (a.timestamp === b.timestamp) {
			merged.push(a);
			i++;
			j++;
		} else if (a.timestamp < b.timestamp) {
			merged.push(a);
			i++;
		} else {
			merged.push(b);
			j++;
		}
	}
	while (i < existing.length) merged.push(existing[i++]);
	while (j < incoming.length) merged.push(incoming[j++]);

	const lastTimestamp = merged.length > 0 ? merged[merged.length - 1].timestamp : watermarkFloorMs;
	return { merged, watermarkMs: Math.max(watermarkFloorMs, lastTimestamp) };
};

/** Replaces the trailing portion of a timeline with freshly-fetched segments: keeps
 *  `existing` segments that start before `fetchStartMs` and appends all of `fetched`.
 *  Always returns a new array (never mutates `existing`) — timeline segments landing in
 *  DynamoDB ~15min late mean the trailing window is periodically re-fetched wholesale
 *  rather than merged point-by-point. */
export const replaceTimelineTail = (
	existing: RobotTimeline[],
	fetched: RobotTimeline[],
	fetchStartMs: number,
): RobotTimeline[] => {
	const kept = existing.filter((segment) => segment.start < fetchStartMs);
	return [...kept, ...fetched];
};
